import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { StatCard, Spinner } from "@/components/shared";
import { WelcomeChecklist } from "@/components/WelcomeChecklist";
import {
  DollarSign, Wallet, FileClock, Clock, Users, ShoppingBag,
  AlertTriangle, ArrowRight, Sparkles,
} from "lucide-react";

export default function Dashboard() {
  const { store } = useStore();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, [store?.id]);

  if (!data) return <Spinner label="Loading your store…" />;
  const cur = store?.currency || "USD";
  const k = data.kpis, m = data.month_settlement;

  const startSettlement = () => navigate("/settlements?start=1");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
        <p className="text-slate-500 mt-1 text-sm">Financial snapshot for {store?.name}</p>
      </div>

      <WelcomeChecklist />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard testid="stat-card-total-sales" label="Total Sales This Month" value={money(k.total_sales, cur)} icon={DollarSign} accent="emerald" />
        <StatCard testid="stat-card-amount-owed" label="Consignor Amount Owed" value={money(k.amount_owed, cur)} icon={Wallet} accent="amber" />
        <StatCard testid="stat-card-pending-settlements" label="Pending Settlements" value={money(k.pending_settlements, cur)} icon={FileClock} accent="blue" />
        <StatCard testid="stat-card-items-expiring-soon" label="Items Expiring Soon" value={k.items_expiring_soon} icon={Clock} accent="rose" />
        <StatCard testid="stat-card-active-consignors" label="Active Consignors" value={k.active_consignors} icon={Users} accent="emerald" />
        <StatCard testid="stat-card-items-sold-month" label="Items Sold This Month" value={k.items_sold_month} icon={ShoppingBag} accent="slate" />
      </div>

      {/* Exception center */}
      {data.exceptions?.length > 0 && (
        <div className="bg-amber-50/80 border border-amber-200/90 rounded-xl p-5" data-testid="exception-center">
          <div className="flex items-center gap-2 text-amber-900 font-bold mb-3">
            <AlertTriangle className="w-5 h-5" /> Items Requiring Attention
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {data.exceptions.map((ex, i) => (
              <button key={i} data-testid={`exception-${ex.type}`} onClick={() => navigate(ex.link)}
                className="flex items-center justify-between gap-3 bg-white/70 hover:bg-white border border-amber-200 rounded-lg px-4 py-3 text-left transition group">
                <span className="text-sm text-slate-700">{ex.message}</span>
                <ArrowRight className="w-4 h-4 text-amber-600 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Month-end settlement */}
      <div className="bg-gradient-to-br from-emerald-900 via-slate-900 to-slate-950 text-white rounded-2xl p-6 sm:p-8 shadow-xl border border-emerald-800/50" data-testid="month-end-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-emerald-400 text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4" /> Month-End Settlement</p>
            <h2 className="font-display text-2xl font-bold mt-1">{data.period_label}</h2>
          </div>
          <button data-testid="start-settlement-btn" onClick={startSettlement}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 py-3 rounded-lg shadow-lg transition flex items-center gap-2">
            Start Settlement <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 pt-6 border-t border-slate-800/80 mt-6">
          {[
            { l: "Gross Sales", v: money(m.gross_sales, cur), c: "text-white" },
            { l: "Discounts", v: `-${money(m.discounts, cur)}`, c: "text-rose-300" },
            { l: "Returns", v: `-${money(m.returns, cur)}`, c: "text-rose-300" },
            { l: "Net Eligible Sales", v: money(m.net_eligible, cur), c: "text-white" },
            { l: "Consignor Share", v: money(m.consignor_share, cur), c: "text-amber-300" },
            { l: "Store Share", v: money(m.store_share, cur), c: "text-emerald-300" },
          ].map((x) => (
            <div key={x.l}>
              <p className="text-[11px] uppercase tracking-wider text-slate-400">{x.l}</p>
              <p className={`font-mono-fin text-lg sm:text-xl font-bold mt-1 ${x.c}`}>{x.v}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
